import {
  type RoomNameMatcherOptions,
  distinctiveTokens,
  findMatchingRoomNames,
  isAllRoomsName,
  stripRoomNameNoise,
  tokenize,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/shared/roomNameMatcher';
import {
  getEgsMarginPercent,
  isArchivedRow,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/egs/egsUtils';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { RoomAssetLink } from '../packageOptions/types';

import { isOtherBedDescribed } from './egsValidation';
import { normalizeRoomsValue } from './egsWizardEngine';
import { EGS_BEDDING_OPTIONS, EGS_COLLECTED_BY_OPTIONS, EGS_GUEST_TYPE_OPTIONS } from './fields';
import type { EgsBeddingType, EgsDraftInput, EgsGuestType } from './types';

export type PreviousEgs = {
  records: Array<EgsDraftInput>;
  rooms: Array<RoomAssetLink>;
  beddingToChoose: number;
  paidToHotel: number;
  otherCollectedBy: number;
  otherGuestType: number;
  otherCurrency: number;
};

export type EgsRow = {
  key: string;
  name: string;
  guestTypes: Array<EgsGuestType>;
  suggestedRooms: Array<string>;
  beddings: Array<EgsBeddingType>;
  bedToChoose: boolean;
  suggestedBed: ExtraBed | '';
};

export type EgsChoices = {
  roomsByNameKey: Record<string, Array<string>>;
  bedByNameKey: Record<string, BedChoice>;
  otherBedByNameKey: Record<string, string>;
  skippedNameKeys: ReadonlySet<string>;
};

// What Salesforce's "Extra (Roll-away/ Sofa Bed/ Daybed)" can mean on the EGS tab.
export const EXTRA_BEDS = [
  'Rollaway bed',
  'Daybed',
  'Single sofa bed',
  'Double sofa bed',
] as const satisfies ReadonlyArray<EgsBeddingType>;

export type ExtraBed = (typeof EXTRA_BEDS)[number];

export const BED_CHOICES = [...EXTRA_BEDS, 'Other'] as const;

export type BedChoice = (typeof BED_CHOICES)[number] | '';

const EXTRA_BED_WORDS: Record<ExtraBed, RegExp> = {
  'Rollaway bed': /\broll[\s-]*away|\bfold[\s-]*(out|away)\b/i,
  Daybed: /\bday[\s-]*bed/i,
  'Single sofa bed': /\bsingle\s+sofa/i,
  'Double sofa bed': /\b(double|queen)\s+sofa/i,
};
const UNSIZED_SOFA_BED = /(?<!\b(single|double|queen)\s+)\bsofa[\s-]*bed/i;

const AGE_RANGE = /^\s*(\d+)?\s*-\s*(\d+)?\s*$/;
const MIN_AGE = 0;
const MAX_AGE = 99;
const PAID_TO_HOTEL_LABEL = 'Paid directly to the Hotel';
const ALL_OF_KIND = /^\s*all\b(.*)$/i;
const MATCHER_OPTIONS: RoomNameMatcherOptions = { plurals: true };

export function nameKey(name: string | undefined) {
  return (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function optionOf<Option extends string>(options: ReadonlyArray<Option>, value: string | null | undefined) {
  return options.find((option) => option === value?.trim());
}

function amount(value: number | string | null | undefined): number {
  return Number(value) || 0;
}

function ageRange(value: string | null | undefined) {
  const [, fromAge, toAge] = value?.match(AGE_RANGE) ?? [];
  return { fromAge: fromAge ? Number(fromAge) : MIN_AGE, toAge: toAge ? Number(toAge) : MAX_AGE };
}

// Salesforce EGS only name their rooms, so match on names and return the ids of every room they read as.
function roomsOf(name: string, rooms: Array<RoomAssetLink>): 'all' | Array<string> {
  if (isAllRoomsName(name)) {
    return 'all';
  }
  const [, kind] = name.match(ALL_OF_KIND) ?? [];
  if (kind !== undefined) {
    const kindTokens = distinctiveTokens(stripRoomNameNoise(kind), MATCHER_OPTIONS);
    if (!kindTokens.length) {
      return 'all';
    }
    return rooms
      .filter((room) => {
        const roomTokens = tokenize(room.name, MATCHER_OPTIONS);
        return kindTokens.every((token) => roomTokens.includes(token));
      })
      .map((room) => room.id);
  }
  const matched = findMatchingRoomNames(
    stripRoomNameNoise(name),
    rooms.map((room) => room.name),
    MATCHER_OPTIONS,
  );
  return rooms.filter((room) => matched.includes(room.name)).map((room) => room.id);
}

export function reducePreviousEgs(
  surcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
  currency: string,
  rooms: Array<RoomAssetLink>,
): PreviousEgs {
  const result: PreviousEgs = {
    records: [],
    rooms,
    beddingToChoose: 0,
    paidToHotel: 0,
    otherCollectedBy: 0,
    otherGuestType: 0,
    otherCurrency: 0,
  };

  for (const surcharge of surcharges) {
    if (isArchivedRow(surcharge)) {
      continue;
    }

    const guestType = optionOf(EGS_GUEST_TYPE_OPTIONS, surcharge.extra_guest_type__c);
    const collectedBy = optionOf(EGS_COLLECTED_BY_OPTIONS, surcharge.collected_by__c);
    const costPrice = amount(surcharge.cost_price__c);
    const sellPriceAud = amount(surcharge.sell_price_aud__c);

    if (!guestType) {
      result.otherGuestType += 1;
      continue;
    }
    if (!collectedBy) {
      if (surcharge.collected_by__c?.trim() === PAID_TO_HOTEL_LABEL) {
        result.paidToHotel += 1;
      } else {
        result.otherCollectedBy += 1;
      }
      continue;
    }
    if (surcharge.cost_price_currency !== currency && costPrice > 0) {
      result.otherCurrency += 1;
      continue;
    }

    const beddingType = optionOf(EGS_BEDDING_OPTIONS, surcharge.bedding_type__c) ?? '';
    if (!beddingType) {
      result.beddingToChoose += 1;
    }
    const convertedCost = amount(surcharge.converted_cost__c);

    result.records.push({
      rooms: roomsOf(surcharge.name ?? '', rooms),
      guestType,
      ...ageRange(surcharge.age_range__c),
      collectedBy,
      beddingType,
      otherBeddingType: surcharge.other_extra_bedding_type__c?.trim() ?? '',
      rateType: '',
      inclusions: surcharge.inclusions__c?.trim() ?? '',
      currency,
      costPrice,
      convertedCost,
      sellPriceAud,
      marginPct: getEgsMarginPercent(convertedCost, sellPriceAud),
      sourceName: surcharge.name?.trim(),
    });
  }

  return result;
}

// Suggested only when the inclusions name one bed: most name none, and "sofa bed" alone could be either size.
function extraBedNamedIn(inclusions: Array<string>): ExtraBed | '' {
  if (inclusions.some((text) => UNSIZED_SOFA_BED.test(text))) {
    return '';
  }
  const named = EXTRA_BEDS.filter((bed) => inclusions.some((text) => EXTRA_BED_WORDS[bed].test(text)));
  return named.length === 1 ? named[0] : '';
}

export function previousEgsRows({ records, rooms }: PreviousEgs): Array<EgsRow> {
  const rows = new Map<string, EgsRow>();
  const bedInclusions = new Map<string, Array<string>>();
  for (const record of records) {
    const key = nameKey(record.sourceName);
    const row = rows.get(key) ?? {
      key,
      name: record.sourceName ?? '',
      guestTypes: [],
      suggestedRooms: record.rooms === 'all' ? rooms.map((room) => room.id) : record.rooms,
      beddings: [],
      bedToChoose: false,
      suggestedBed: '',
    };
    if (!row.guestTypes.includes(record.guestType)) {
      row.guestTypes.push(record.guestType);
    }
    if (!record.beddingType) {
      row.bedToChoose = true;
      bedInclusions.set(key, [...(bedInclusions.get(key) ?? []), record.inclusions]);
    } else if (!row.beddings.includes(record.beddingType)) {
      row.beddings.push(record.beddingType);
    }
    rows.set(key, row);
  }
  return [...rows.values()].map((row) => ({ ...row, suggestedBed: extraBedNamedIn(bedInclusions.get(row.key) ?? []) }));
}

export function chosenEgsBeds(rows: Array<EgsRow>, picked: Record<string, BedChoice>): Record<string, BedChoice> {
  return Object.fromEntries(rows.map((row) => [row.key, picked[row.key] ?? row.suggestedBed]));
}

export function everyOtherBedDescribed(
  rows: Array<EgsRow>,
  { bedByNameKey, otherBedByNameKey, skippedNameKeys }: Omit<EgsChoices, 'roomsByNameKey'>,
): boolean {
  return rows.every(
    (row) =>
      skippedNameKeys.has(row.key) ||
      isOtherBedDescribed(bedByNameKey[row.key] ?? '', otherBedByNameKey[row.key] ?? ''),
  );
}

export function chosenEgsRooms(
  rows: Array<EgsRow>,
  picked: Record<string, Array<string>>,
  roomIds: Array<string>,
): Record<string, Array<string>> {
  return Object.fromEntries(
    rows.map((row) => [row.key, (picked[row.key] ?? row.suggestedRooms).filter((room) => roomIds.includes(room))]),
  );
}

export function withEgsChoices(
  { records, rooms }: PreviousEgs,
  { roomsByNameKey, bedByNameKey, otherBedByNameKey, skippedNameKeys }: EgsChoices,
): Array<EgsDraftInput> {
  return records
    .filter((record) => !skippedNameKeys.has(nameKey(record.sourceName)))
    .map((record) => {
      const key = nameKey(record.sourceName);
      const withRooms = {
        ...record,
        rooms: normalizeRoomsValue(
          roomsByNameKey[key] ?? [],
          rooms.map((room) => room.id),
        ),
      };
      if (record.beddingType) {
        return withRooms;
      }
      const bed = bedByNameKey[key] ?? '';
      return {
        ...withRooms,
        beddingType: bed,
        otherBeddingType: bed === 'Other' ? (otherBedByNameKey[key] ?? '').trim() : record.otherBeddingType,
      };
    });
}
